/**
 * Build the API base URL for an organization
 * @param {String|Number|Function} orgId - Organization ID or getter function
 */
function getOrgApiBase(orgId) {
    const id = typeof orgId === 'function' ? orgId() : orgId;
    return `/api/${id}`;
}

/**
 * Factory function for RESTful API resources
 * Matches Laravel's Route::apiResource() pattern
 *
 * @param {String} resource - Resource name (e.g., 'contacts', 'products')
 * @param {Object} options - Configuration options
 * @param {String|Number|Function} options.orgId - Organization ID (required for org resources)
 *
 * @example
 * // In organization views - pass orgId from route
 * const api = useApi('contacts', { orgId: route.params.org })
 * await api.index()  // GET /api/org/{orgId}/contacts
 *
 * // For non-org resources (profile, auth, etc.)
 * const api = useApi('user')  // GET /api/user
 */
export function useApi(resource, options = {}) {
    const { orgId } = options;

    /**
     * Get the base URL for this resource
     */
    function baseUrl() {
        if (orgId) {
            return `${getOrgApiBase(orgId)}/${resource}`;
        }
        return `/api/${resource}`;
    }

    return {
        /**
         * GET /resource - List all resources
         * @param {Object} params - Query parameters
         */
        index(params = {}) {
            return axios.get(baseUrl(), { params });
        },

        /**
         * GET /resource/:id - Show a single resource
         * @param {Number|String} id - Resource ID
         */
        show(id) {
            return axios.get(`${baseUrl()}/${id}`);
        },

        /**
         * POST /resource - Create a new resource
         * @param {Object} data - Resource data
         */
        store(data) {
            return axios.post(baseUrl(), data);
        },

        /**
         * PUT /resource/:id - Update a resource
         * @param {Number|String} id - Resource ID
         * @param {Object} data - Resource data
         */
        update(id, data) {
            return axios.put(`${baseUrl()}/${id}`, data);
        },

        /**
         * PATCH /resource/:id - Partially update a resource
         * @param {Number|String} id - Resource ID
         * @param {Object} data - Resource data
         */
        patch(id, data) {
            return axios.patch(`${baseUrl()}/${id}`, data);
        },

        /**
         * DELETE /resource/:id - Delete a resource
         * @param {Number|String} id - Resource ID
         */
        destroy(id) {
            return axios.delete(`${baseUrl()}/${id}`);
        },

        /**
         * Custom GET request
         * @param {String} path - Additional path segment
         * @param {Object} params - Query parameters
         */
        get(path, params = {}) {
            return axios.get(`${baseUrl()}/${path}`, { params });
        },

        /**
         * Custom POST request
         * @param {String} path - Additional path segment
         * @param {Object} data - Request data
         */
        post(path, data = {}) {
            return axios.post(`${baseUrl()}/${path}`, data);
        },

        /**
         * Get the base URL (for custom operations)
         */
        getBaseUrl() {
            return baseUrl();
        }
    };
}

/**
 * Create an API instance for a nested resource
 *
 * @param {String} parentResource - Parent resource name
 * @param {Number|String|Function} parentId - Parent ID (can be a getter function)
 * @param {String} resource - Child resource name
 * @param {Object} options - Configuration options
 * @param {String|Number|Function} options.orgId - Organization ID (required for org resources)
 *
 * @example
 * // /api/{orgId}/products/{productId}/suppliers
 * const api = useNestedApi('products', () => props.id, 'suppliers', { orgId: route.params.org })
 */
export function useNestedApi(parentResource, parentId, resource, options = {}) {
    const { orgId } = options;

    /**
     * Get the parent ID value
     */
    function getParentId() {
        return typeof parentId === 'function' ? parentId() : parentId;
    }

    /**
     * Get the base URL for this nested resource
     */
    function baseUrl() {
        const base = orgId ? getOrgApiBase(orgId) : '/api';
        return `${base}/${parentResource}/${getParentId()}/${resource}`;
    }

    return {
        index(params = {}) {
            return axios.get(baseUrl(), { params });
        },

        show(id) {
            return axios.get(`${baseUrl()}/${id}`);
        },

        store(data) {
            return axios.post(baseUrl(), data);
        },

        update(id, data) {
            return axios.put(`${baseUrl()}/${id}`, data);
        },

        patch(id, data) {
            return axios.patch(`${baseUrl()}/${id}`, data);
        },

        destroy(id) {
            return axios.delete(`${baseUrl()}/${id}`);
        },

        get(path, params = {}) {
            return axios.get(`${baseUrl()}/${path}`, { params });
        },

        post(path, data = {}) {
            return axios.post(`${baseUrl()}/${path}`, data);
        },

        getBaseUrl() {
            return baseUrl();
        }
    };
}
